/**
 * Shared platform/session/editor state handed down from the protected layout
 * route ({@link AuthenticatedWorkspace}) to its routed children via
 * `<Outlet context={...} />`. This is the route-context mechanism the
 * architecture requirements call for, so nested routes read shared state
 * instead of re-fetching it or duplicating the API client/session providers.
 *
 * @module router/workspace-context
 */

import React, { createContext, useContext } from "react";

import { useOutletContext } from "react-router";

import type { ShellPresentationProfile } from "@/components/shared/workspace-types";
import type {
  AssignedModuleSummary,
  EditorProgrammeSyncValue,
  PlatformAdminOverview,
  PortfolioSummary,
  ProgrammePlatformClient,
  ProgrammeSummary,
  SessionContext,
  UserSummary,
} from "@/platform";
import type { AuthorizationSubject } from "@/server/authorization";
import type { ProviderSettings } from "@/types/provider";
import type { RegionPreference } from "@/utils/format-instant";

export interface WorkspacePermissions {
  isPlatformAdmin: boolean;
  canEditProgramme: boolean;
  canEditWorkflowState: boolean;
  canEditCurrentProgramme: boolean;
  shouldRenderReadOnlyEditorView: boolean;
  /** The open programme was opened to view, so nothing in it may change. */
  viewingProgramme: boolean;
  canManageTenant: boolean;
  canAccessWorkspace: boolean;
  canQuickCreateProgramme: boolean;
  canQuickCreateModule: boolean;
  canManageProgrammeTeam: boolean;
  canViewReports: boolean;
  /**
   * The tenant setting, not a permission: a tenant admin manages a team
   * whatever it says. The shell loads it with the rest of the workspace and
   * refreshes it, so a screen reading it here follows an admin turning it off.
   */
  allowLeadTeamManagement: boolean;
}

export interface WorkspaceOutletContext extends WorkspacePermissions {
  client: ProgrammePlatformClient;
  session: SessionContext;
  profile: ShellPresentationProfile;
  subject: AuthorizationSubject;
  editorSync: EditorProgrammeSyncValue;
  editLeasesEnabled: boolean;
  platformProgrammes: ProgrammeSummary[];
  platformModules: AssignedModuleSummary[];
  platformUsers: UserSummary[];
  platformPortfolio: PortfolioSummary | null;
  platformDisciplines: readonly string[];
  /** The tenant's own locale and zone, which a person may override. */
  tenantRegion: RegionPreference;
  /** The tenant's provider settings, `null` for a platform admin or before they load. */
  tenantProviderSettings: ProviderSettings | null;
  platformAdminOverview: PlatformAdminOverview | null;
  platformDataError: Error | null;
  refreshPlatformWorkspace: () => Promise<void>;
  refreshSession: () => Promise<void>;
  handleWorkflowCompleted: (programmeId: string) => Promise<void>;
  /** Open a programme to view, or back to editing where the account may. */
  setProgrammeViewMode: (programmeId: string, viewing: boolean) => void;
}

/**
 * The same context, reachable from the shell's own chrome.
 *
 * The top bar and the sidebar are siblings of the `<Outlet>`, not descendants,
 * so `useOutletContext` returns nothing there. A route naming a component for
 * the top bar's action slot still needs the permissions this carries.
 */
const WorkspaceContext = createContext<WorkspaceOutletContext | null>(null);

export interface WorkspaceProviderProps {
  value: WorkspaceOutletContext;
  children: React.ReactNode;
}

/** Wraps the whole shell, chrome included. */
export function WorkspaceProvider({ value, children }: WorkspaceProviderProps) {
  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}

/**
 * The same context, for chrome that has something to render without it.
 *
 * The breadcrumb is the case: it names whatever the router matched, and only
 * enriches a crumb with a title when the workspace happens to be loaded. Making
 * it throw would couple a static trail to the whole workspace.
 *
 * @returns The context, or `null` outside a provider.
 */
export function useOptionalWorkspaceContext(): WorkspaceOutletContext | null {
  const outlet = useOutletContext<WorkspaceOutletContext | null>();
  const provided = useContext(WorkspaceContext);
  // The provider first: it wraps the whole shell with the same value, while
  // `useOutletContext` returns whatever the *nearest* outlet passed, and the
  // editor's step shell passes its own. That one is truthy with every field
  // absent, so the guard below stayed quiet and permissions read as denied.
  return provided ?? outlet;
}

/**
 * Read the shared workspace context provided by {@link AuthenticatedWorkspace}.
 *
 * Throws rather than handing back an absent context: every field on it is
 * destructured at the call site, so the failure would otherwise surface as
 * "cannot destructure property" from whichever field happened to be first.
 */
export function useWorkspaceOutletContext(): WorkspaceOutletContext {
  const context = useOptionalWorkspaceContext();
  if (!context) {
    throw new Error("useWorkspaceOutletContext must be used within <AuthenticatedWorkspace>");
  }
  return context;
}
